import { Link } from 'react-router-dom';
import type { CaseItem } from '@/mocks/cases';

interface Props {
  item: CaseItem;
}

export default function CaseDetailContent({ item }: Props) {
  return (
    <div className="bg-[#fafaf8]">
      {/* Hero Image */}
      <div className="w-full h-[55vh] md:h-[70vh] relative overflow-hidden">
        <img
          src={item.coverImg}
          alt={item.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 max-w-[1440px] mx-auto px-6 md:px-10 pb-12">
          <span className="inline-block px-3 py-1 bg-[#b8956a] text-white text-xs font-medium rounded-full mb-4">
            {item.category}
          </span>
          <h1
            className="text-white font-bold leading-[1.25] mb-2"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
          >
            {item.title}
          </h1>
          <p className="text-white/70" style={{ fontSize: '1.1875rem' }}>{item.subtitle}</p>
        </div>
      </div>

      {/* Meta Info Strip */}
      <div className="bg-[#1a1a1a]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-6">
          <div className="flex flex-wrap gap-8 md:gap-12">
            {[
              { icon: 'ri-map-pin-line', label: '地點', value: item.location },
              { icon: 'ri-aspect-ratio-line', label: '坪數', value: item.area },
              { icon: 'ri-time-line', label: '工期', value: item.duration },
              { icon: 'ri-calendar-line', label: '完工年份', value: item.year },
            ].map((m) => (
              <div key={m.label} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${m.icon} text-[#b8956a] text-base`}></i>
                </div>
                <div>
                  <p className="text-white/40 text-xs">{m.label}</p>
                  <p className="text-white text-sm font-medium">{m.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
        {/* Background */}
        <div className="max-w-3xl mb-16">
          <p className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase mb-4">Project Background</p>
          <h2
            className="font-bold text-[#1a1a1a] leading-[1.35] mb-5"
            style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.875rem)' }}
          >
            專案背景
          </h2>
          <p className="text-[#5a5a5a] leading-[1.75]" style={{ fontSize: '1.1875rem' }}>
            {item.background}
          </p>
        </div>

        {/* Problem + Solution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Problem */}
          <div className="bg-[#f4f2ef] rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#e2ddd8] rounded-xl">
                <i className="ri-error-warning-line text-[#8a8a8a] text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-[#8a8a8a] uppercase tracking-widest">Pain Points</p>
                <h3 className="font-semibold text-[#1a1a1a]" style={{ fontSize: '1.125rem' }}>
                  業主面臨的問題
                </h3>
              </div>
            </div>
            <ul className="space-y-4">
              {item.problem.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#b8956a] block"></span>
                  </div>
                  <p className="text-[#5a5a5a] leading-relaxed" style={{ fontSize: '1rem' }}>{p}</p>
                </li>
              ))}
            </ul>
          </div>

          {/* Solution */}
          <div className="bg-[#1a1a1a] rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#b8956a]/20 rounded-xl">
                <i className="ri-lightbulb-flash-line text-[#b8956a] text-lg"></i>
              </div>
              <div>
                <p className="text-xs text-[#b8956a] uppercase tracking-widest">Our Solution</p>
                <h3 className="font-semibold text-white" style={{ fontSize: '1.125rem' }}>
                  設計解決方案
                </h3>
              </div>
            </div>
            <ul className="space-y-4">
              {item.solution.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#b8956a] text-sm"></i>
                  </div>
                  <p className="text-white/70 leading-relaxed" style={{ fontSize: '1rem' }}>{s}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Photo Gallery */}
        <div className="mb-16">
          <p className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase mb-4">Project Gallery</p>
          <h2
            className="font-bold text-[#1a1a1a] leading-[1.35] mb-8"
            style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.875rem)' }}
          >
            空間實景
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {item.detailImgs.map((img, i) => (
              <div key={i} className="w-full h-[260px] md:h-[300px] rounded-xl overflow-hidden">
                <img
                  src={img}
                  alt={`${item.title} 空間 ${i + 1}`}
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Highlights */}
        <div className="mb-16">
          <p className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase mb-4">Space Highlights</p>
          <h2
            className="font-bold text-[#1a1a1a] leading-[1.35] mb-8"
            style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.875rem)' }}
          >
            空間亮點
          </h2>
          <div className="flex flex-wrap gap-3">
            {item.highlight.map((h) => (
              <span
                key={h}
                className="px-5 py-2.5 bg-[#f4f2ef] border border-[#e2ddd8] text-[#1a1a1a] font-medium rounded-full"
                style={{ fontSize: '1rem' }}
              >
                {h}
              </span>
            ))}
          </div>
        </div>

        {/* Result */}
        <div className="bg-[#b8956a]/10 border border-[#b8956a]/30 rounded-2xl p-8 md:p-10 mb-16">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center bg-[#b8956a] rounded-xl">
              <i className="ri-line-chart-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-xs text-[#b8956a] uppercase tracking-widest">Project Result</p>
              <h3 className="font-semibold text-[#1a1a1a]" style={{ fontSize: '1.125rem' }}>
                完工後帶來的改變
              </h3>
            </div>
          </div>
          <p className="text-[#3a3a3a] leading-[1.75]" style={{ fontSize: '1.1875rem' }}>
            {item.result}
          </p>
        </div>

        {/* Testimonial */}
        <div className="bg-[#1a1a1a] rounded-2xl p-8 md:p-12 mb-16">
          <i className="ri-double-quotes-l text-[#b8956a] text-4xl block mb-5"></i>
          <p
            className="text-white/85 leading-[1.75] mb-8 max-w-3xl"
            style={{ fontSize: '1.1875rem' }}
          >
            {item.testimonial}
          </p>
          <div className="flex items-center gap-4">
            <div className="w-12 h-px bg-[#b8956a]"></div>
            <div>
              <p className="text-white font-semibold">{item.testimonialAuthor}</p>
              <p className="text-[#b8956a] text-sm mt-0.5">{item.testimonialRole}</p>
            </div>
          </div>
        </div>

        {/* Back + CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-5 pt-8 border-t border-[#e2ddd8]">
          <Link
            to="/cases"
            className="inline-flex items-center gap-2 text-[#5a5a5a] hover:text-[#1a1a1a] transition-colors cursor-pointer"
            style={{ fontSize: '1rem' }}
          >
            <i className="ri-arrow-left-line"></i>
            返回全部案例
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#b8956a] text-white font-semibold rounded-full whitespace-nowrap hover:bg-[#a07d58] transition-colors cursor-pointer"
            style={{ fontSize: '1.0625rem' }}
          >
            <i className="ri-calendar-check-line"></i>
            預約免費諮詢
          </Link>
        </div>
      </div>
    </div>
  );
}
